/**
 * Audio extraction module
 *
 * Provides high-performance audio extraction from video files with fallback support:
 * 1. WebCodecs (fastest, for MP4/MOV on Chrome/Safari/Edge)
 * 2. FFmpeg WASM (all formats, requires SharedArrayBuffer)
 * 3. Passthrough (direct upload, server-side processing)
 */

import { selectStrategy } from './strategies';
import { estimateMediaDuration } from './duration';
import {
  isWebCodecsSupported,
  isSharedArrayBufferSupported,
  getBrowserCapabilities,
} from './capabilities';

import type {
  PreparedAudioUpload,
  AudioExtractionProgress,
  AudioExtractionStage,
  ExtractionStrategy,
} from './types';

export type {
  PreparedAudioUpload,
  AudioExtractionProgress,
  AudioExtractionStage,
  ExtractionStrategy,
};

/**
 * Prepare a file for audio upload
 * - Audio files are returned as-is
 * - Video files are converted to audio using the best available strategy
 *
 * @param file - The file to prepare
 * @param onProgress - Optional progress callback
 * @returns PreparedAudioUpload with the processed file
 */
export async function prepareAudioUpload(
  file: File,
  onProgress?: (progress: AudioExtractionProgress) => void
): Promise<PreparedAudioUpload> {
  // Estimate duration (best effort, may fail for some formats)
  const estimatedDurationSeconds = await estimateMediaDuration(file);

  // Audio files don't need extraction
  if (file.type.startsWith('audio/')) {
    console.log(
      `%c[AudioExtraction] %cSkipped (audio file)`,
      'color: #6366f1; font-weight: bold',
      'color: inherit',
      { file: file.name, type: file.type }
    );
    return {
      file,
      estimatedDurationSeconds,
      extractionStrategy: 'none',
    };
  }

  // Select the best available strategy
  const strategy = await selectStrategy(file);
  const startTime = performance.now();

  console.log(
    `%c[AudioExtraction] %cStarting with %c${strategy.name}`,
    'color: #6366f1; font-weight: bold',
    'color: inherit',
    'color: #10b981; font-weight: bold',
    { file: file.name, size: `${(file.size / 1024 / 1024).toFixed(2)} MB` }
  );

  try {
    const audioFile = await strategy.extract(file, onProgress);
    const elapsed = ((performance.now() - startTime) / 1000).toFixed(2);

    console.log(
      `%c[AudioExtraction] %c✓ Completed with %c${strategy.name}%c in %c${elapsed}s`,
      'color: #6366f1; font-weight: bold',
      'color: #10b981',
      'color: #10b981; font-weight: bold',
      'color: #10b981',
      'color: #f59e0b; font-weight: bold',
      {
        input: `${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`,
        output: `${audioFile.name} (${(audioFile.size / 1024 / 1024).toFixed(2)} MB)`,
        compression: `${((1 - audioFile.size / file.size) * 100).toFixed(1)}%`,
      }
    );

    return {
      file: audioFile,
      estimatedDurationSeconds,
      extractionStrategy: strategy.name,
    };
  } catch (error) {
    const elapsed = ((performance.now() - startTime) / 1000).toFixed(2);
    console.warn(
      `%c[AudioExtraction] %c✗ ${strategy.name} failed after ${elapsed}s, trying fallback:`,
      'color: #6366f1; font-weight: bold',
      'color: #ef4444',
      error
    );

    // If primary strategy fails, try fallback (excluding the failed one)
    if (strategy.name !== 'passthrough') {
      const fallbackStrategy = await selectStrategy(file, {
        exclude: [strategy.name],
      });
      const fallbackStartTime = performance.now();

      console.log(
        `%c[AudioExtraction] %cFallback to %c${fallbackStrategy.name}`,
        'color: #6366f1; font-weight: bold',
        'color: inherit',
        'color: #f59e0b; font-weight: bold'
      );

      try {
        const audioFile = await fallbackStrategy.extract(file, onProgress);
        const fallbackElapsed = ((performance.now() - fallbackStartTime) / 1000).toFixed(2);

        console.log(
          `%c[AudioExtraction] %c✓ Fallback completed with %c${fallbackStrategy.name}%c in %c${fallbackElapsed}s`,
          'color: #6366f1; font-weight: bold',
          'color: #10b981',
          'color: #10b981; font-weight: bold',
          'color: #10b981',
          'color: #f59e0b; font-weight: bold'
        );

        return {
          file: audioFile,
          estimatedDurationSeconds,
          extractionStrategy: fallbackStrategy.name,
        };
      } catch (fallbackError) {
        console.warn(
          `%c[AudioExtraction] %c✗ ${fallbackStrategy.name} also failed:`,
          'color: #6366f1; font-weight: bold',
          'color: #ef4444',
          fallbackError
        );

        // Final fallback: passthrough if not already tried
        if (fallbackStrategy.name !== 'passthrough') {
          const passthroughStrategy = await selectStrategy(file, {
            exclude: [strategy.name, fallbackStrategy.name],
          });

          console.log(
            `%c[AudioExtraction] %cFinal fallback to %c${passthroughStrategy.name}`,
            'color: #6366f1; font-weight: bold',
            'color: inherit',
            'color: #f59e0b; font-weight: bold'
          );

          const audioFile = await passthroughStrategy.extract(file, onProgress);
          return {
            file: audioFile,
            estimatedDurationSeconds,
            extractionStrategy: passthroughStrategy.name,
          };
        }

        throw fallbackError;
      }
    }

    throw error;
  }
}

/**
 * Check if any audio extraction method is supported
 * Returns true if either WebCodecs or FFmpeg WASM is available
 *
 * @returns boolean indicating if extraction is supported
 */
export function isAudioExtractionSupported(): boolean {
  return isWebCodecsSupported() || isSharedArrayBufferSupported();
}

/**
 * Estimate media duration in seconds
 * Re-exported for backward compatibility
 */
export { estimateMediaDuration as estimateMediaDurationSeconds };

/**
 * Get detailed browser capabilities
 */
export { getBrowserCapabilities };
